import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Avatar,
} from '@mui/material';
import { CalendarToday, Email, Refresh, Logout } from '@mui/icons-material';

import { authService } from '@/services/authService';
import { useAuth } from '@/hooks/useAuth';

const VerifyEmailPendingPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleResend = async () => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      await authService.resendVerification();
      setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to resend verification email');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Avatar sx={{
          m: 1,
          width: 56,
          height: 56,
          bgcolor: '#10b981'
        }}>
          <CalendarToday fontSize="large" />
        </Avatar>
        <Typography component="h1" variant="h4" gutterBottom>
          Player Central
        </Typography>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Verify your email
        </Typography>

        <Card sx={{ mt: 3, width: '100%' }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Email color="primary" sx={{ fontSize: 64, mb: 2 }} />

            <Typography variant="h6" gutterBottom>
              Check your email
            </Typography>

            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              We sent a verification email to:
            </Typography>

            <Typography variant="body1" fontWeight="bold" sx={{ mb: 3 }}>
              {user?.email}
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Click the link in the email to verify your account. The link will expire in 24 hours.
            </Typography>

            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Verification email sent! Check your inbox.
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                variant="contained"
                onClick={handleResend}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <Refresh />}
              >
                {loading ? 'Sending...' : 'Resend Verification Email'}
              </Button>

              <Button
                variant="outlined"
                onClick={handleLogout}
                startIcon={<Logout />}
              >
                Sign Out
              </Button>
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3 }}>
              Didn't receive the email? Check your spam folder or click resend above.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default VerifyEmailPendingPage;
